// src/api/client.js (static-asset safe)
// (Full file with ONLY the requested enhancement: CP default env vars added to ENV)

import { getToken } from "../msal";

function resolveProxyBase() {
    const raw =
        read("VITE_PROXY_BASE", read("REACT_APP_PROXY_BASE", "")).trim();
    if (!raw) throw new Error("PROXY_BASE not configured");
    return normalizeEndpoint(raw);
}

/* ---- build proxy auth headers (JWT) ---- */
async function buildProxyHeaders(extra = {}) {
    // Ensure a VALID, non-expired access token before every proxy call.
    // getAccessTokenAuto() silently refreshes when TTL is low and, if the
    // refresh token itself is expired, getToken() recalls SSO (redirect).
    const token = await getAccessTokenAuto({ minTtlMs: 5 * 60 * 1000 });
    if (!token) throw new Error("No JWT available from MSAL");

    return {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Request-ID": newReqId(),
        ...extra,
    };
}


/* =======================
   TEAMBRIDGE COOKIE SESSION (OIDC -> /api/login -> cookie)
   ======================= */

// Cache so we don't call /api/login on every request
let __tbCookieLogin = {
    inFlight: null,
    lastOkAt: 0,
    lastTokenSig: "",
};

// Persist across reloads/tabs to avoid excessive /api/login calls
const __TB_LOGIN_AT_KEY = "TB_LAST_LOGIN_AT";
const __TB_LOGIN_SIG_KEY = "TB_LAST_LOGIN_SIG";

function __safeStorage() {
    try {
        if (typeof window === "undefined") return null;
        return window.sessionStorage || window.localStorage || null;
    } catch {
        return null;
    }
}

function __loadTbLoginCache() {
    const s = __safeStorage();
    if (!s) return;
    try {
        const at = Number(s.getItem(__TB_LOGIN_AT_KEY) || "0");
        const sig = String(s.getItem(__TB_LOGIN_SIG_KEY) || "");
        if (!__tbCookieLogin.lastOkAt && at) __tbCookieLogin.lastOkAt = at;
        if (!__tbCookieLogin.lastTokenSig && sig) __tbCookieLogin.lastTokenSig = sig;
    } catch {
        // ignore
    }
}

function __saveTbLoginCache() {
    const s = __safeStorage();
    if (!s) return;
    try {
        s.setItem(__TB_LOGIN_AT_KEY, String(__tbCookieLogin.lastOkAt || 0));
        s.setItem(__TB_LOGIN_SIG_KEY, String(__tbCookieLogin.lastTokenSig || ""));
    } catch {
        // ignore
    }
}


// Base64URL decode JWT payload (no validation; only for exp/iat metadata)
function decodeJwtPayload(token) {
    try {
        const parts = String(token || "").split(".");
        if (parts.length < 2) return null;
        const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        const pad = "=".repeat((4 - (b64.length % 4)) % 4);
        const json = atob(b64 + pad);
        return JSON.parse(json);
    } catch {
        return null;
    }
}

/**
 * getAccessTokenAuto()
 * - Returns an access token.
 * - If token expiry is within `minTtlMs`, it attempts MSAL silent refresh (forceRefresh) once.
 */
async function getAccessTokenAuto({ minTtlMs = 5 * 60 * 1000, forceRefresh = false } = {}) {
    const token = await getToken({ forceRefresh: !!forceRefresh });
    if (!token) return "";

    const payload = decodeJwtPayload(token) || {};
    const expMs = payload.exp ? Number(payload.exp) * 1000 : 0;

    if (!expMs) return token; // can't assess; just use it

    const nowMs = Date.now();
    const ttlMs = expMs - nowMs;

    if (forceRefresh || (ttlMs > 0 && ttlMs < minTtlMs)) {
        try {
            const fresh = await getToken({ forceRefresh: true });
            return fresh || token;
        } catch {
            return token;
        }
    }

    return token;
}

function getTokenExpMs(token) {
    try {
        const p = decodeJwtPayload(token) || {};
        return p.exp ? Number(p.exp) * 1000 : 0;
    } catch {
        return 0;
    }
}


function tokenSig(token) {
    const s = String(token || "");
    if (!s) return "";
    return `${s.slice(0, 12)}…${s.slice(-12)}`;
}

function tbCookieLoginUrl() {
    const base = normalizeBase(ENV.TB_COOKIE_BASE);
    return normalizeEndpoint(`${base}/api/login`);
}

/**
 * Calls https://api.dev.directv.com/dtvcs-teambridge-api/api/login with Authorization: Bearer <OIDC/JWT>.
 * Browser will store Set-Cookie automatically (if API sets SameSite=None;Secure and CORS allows credentials).
 *
 * NOTE: In browsers, `Set-Cookie` header is not readable via JS (and HttpOnly cookies are not visible via document.cookie).
 * For now we log the HTTP status + any visible cookies as best-effort debugging.
 */
export async function ensureTeamBridgeCookieSession(force = false) {
    __loadTbLoginCache();

    // Get an access token that has at least 5 minutes of TTL when possible.
    // If force=true we ask MSAL to refresh silently.
    const token = await getAccessTokenAuto({
        minTtlMs: 5 * 60 * 1000,
        forceRefresh: !!force,
    });

    if (!token) throw new Error("No OIDC/JWT token available (MSAL getToken returned empty)");

    const expMs = getTokenExpMs(token);

    // Key the cookie-login cache on token signature + expiry (expiry changes when token refreshes).
    const sig = `${tokenSig(token)}|${expMs || "noexp"}`;

    const now = Date.now();
    // Re-establish the cookie BEFORE the server-side idle timeout elapses.
    // Driven by REACT_APP_IDLE_TIMEOUT_MIN (default 30) minus a safety margin,
    //  so we never fire a request on a cookie that may have just idle-expired.
    const idleMin = Number(ENV?.IDLE_TIMEOUT_MIN) > 0 ? Number(ENV.IDLE_TIMEOUT_MIN) : 30;
    const idleMs = idleMin * 60 * 1000;
    const safetyMs = Math.min(5 * 60 * 1000, Math.floor(idleMs * 0.2));
    const throttleMs = Math.max(60 * 1000, idleMs - safetyMs); // e.g. 30min idle -> re-login after 25min
    const recentlyOk = __tbCookieLogin.lastOkAt && (now - __tbCookieLogin.lastOkAt) < throttleMs;
    const sameToken = __tbCookieLogin.lastTokenSig === sig;

    // If we recently refreshed the TB cookie with the same token, skip.
    if (!force && sameToken && recentlyOk) return true;

    // Serialize concurrent calls (multi-components / multi-tabs)
    if (__tbCookieLogin.inFlight) return __tbCookieLogin.inFlight;

    __tbCookieLogin.inFlight = (async () => {
        const url = tbCookieLoginUrl();
        const r = await fetchWithTimeout(
            url,
            {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "X-Request-ID": newReqId(),
                },
                // critical for cookie set + send
                credentials: "include",
            },
            "tb-login"
        );

        if (!r.ok) {
            const txt = await safeReadText(r);
            throw new Error(
                `TB /api/login failed: ${r.status} ${r.statusText} ${txt ? `| ${txt}` : ""}`
            );
        }

        // Best-effort debug (Set-Cookie not readable in browser)
        // eslint-disable-next-line no-console
        console.log("[TB] /api/login ok:", { status: r.status, url });

        try {
            // eslint-disable-next-line no-console
            //console.log(
            //   "[TB] document.cookie (may be empty if HttpOnly):",
            //  document.cookie || "(empty)"
            //);
        } catch {
            // ignore
        }

        __tbCookieLogin.lastOkAt = Date.now();
        __tbCookieLogin.lastTokenSig = sig;
        __saveTbLoginCache();
        return true;
    })();

    try {
        return await __tbCookieLogin.inFlight;
    } finally {
        __tbCookieLogin.inFlight = null;
    }
}

/*
  Keep the TeamBridge cookie-session warm.

  Why: Browser-managed HttpOnly cookies can disappear/expire or stop being sent
  (idle timeout, short Max-Age, infra). We can't read HttpOnly cookies from JS,
  so the only reliable recovery is:
    1) Re-hit /api/login using current Entra access token
    2) Retry the failed TB/Hinge request once (handled elsewhere)

  This refresher:
    - Schedules a non-forced cookie renew ~N minutes before OIDC token expiry
    - Falls back to a periodic check if expiry can't be parsed
    - Skips background refresh when the tab is hidden
    - Adds jitter to avoid thundering herd
*/

let __tbRefreshTimer = null;

export function startTeamBridgeCookieSessionRefresh() {
    if (__tbRefreshTimer) return;

    const refreshWindowMs = Number(
        read(
            "VITE_TB_TOKEN_REFRESH_WINDOW_MS",
            read("REACT_APP_TB_TOKEN_REFRESH_WINDOW_MS", String(5 * 60 * 1000))
        )
    );

    // Fallback interval when token expiry isn't available
    const fallbackSec = Number(
        read("VITE_TB_COOKIE_REFRESH_SEC", read("REACT_APP_TB_COOKIE_REFRESH_SEC", "300"))
    );
    const fallbackEveryMs =
        Number.isFinite(fallbackSec) && fallbackSec > 0 ? fallbackSec * 1000 : 5 * 60 * 1000;

    const clearTimer = () => {
        try {
            if (__tbRefreshTimer) window.clearTimeout(__tbRefreshTimer);
        } catch {
            // ignore
        }
        __tbRefreshTimer = null;
    };

    const scheduleNext = async () => {
        clearTimer();

        // Avoid background spam; re-check later.
        try {
            if (typeof document !== "undefined" && document.hidden) {
                __tbRefreshTimer = window.setTimeout(scheduleNext, 60 * 1000);
                return;
            }
        } catch {
            // ignore
        }

        let delayMs = fallbackEveryMs;

        try {
            const t = await getAccessTokenAuto({
                minTtlMs: refreshWindowMs,
                forceRefresh: false,
            });
            const expMs = getTokenExpMs(t);
            const nowMs = Date.now();

            if (expMs && expMs > nowMs) {
                const targetMs = expMs - refreshWindowMs;
                // never schedule faster than 60s, even if exp is very close
                delayMs = Math.max(60 * 1000, targetMs - nowMs);
            }
        } catch {
            // ignore; use fallback delay
        }

        // Add jitter (0-30s) to avoid herd
        delayMs += Math.floor(Math.random() * 30 * 1000);

        __tbRefreshTimer = window.setTimeout(async () => {
            try {
                await ensureTeamBridgeCookieSession(false);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.warn("[TB] cookie session refresh failed:", e?.message || e);
            } finally {
                scheduleNext();
            }
        }, delayMs);
    };

    // Start immediately and then schedule based on expiry
    (async () => {
        try {
            await ensureTeamBridgeCookieSession(false);
        } catch {
            // ignore
        } finally {
            scheduleNext();
        }
    })();

    // regain focus => opportunistic refresh (non-forced)
    try {
        window.addEventListener("focus", () => {
            ensureTeamBridgeCookieSession(false).catch(() => { });
        });

        document.addEventListener("visibilitychange", () => {
            if (!document.hidden) ensureTeamBridgeCookieSession(false).catch(() => { });
        });
    } catch {
        // ignore
    }
}

// =========================
// Refresh timer (module scope)
// =========================
async function safeReadBody(res) {
    try {
        // NOTE: Always read as text first so empty bodies ("" / 204) don't throw on JSON.parse.
        const ct = String(res?.headers?.get?.("content-type") || "").toLowerCase();
        const txt = await res.text();
        if (!txt || !txt.trim()) return "";
        if (ct.includes("application/json")) {
            try { return JSON.stringify(JSON.parse(txt)); } catch { return txt; }
        }
        return txt;
    } catch {
        return "";
    }
}

export function stopTeamBridgeCookieSessionRefresh() {
    try {
        if (__tbRefreshTimer) window.clearTimeout(__tbRefreshTimer);
    } catch {
        // ignore
    }
    __tbRefreshTimer = null;
}



/* =======================
   PROXY GET (JWT)
   ======================= */
export async function proxyGetJson(urlOrCfg, extra = {}) {
    const url = toUrl(
        typeof urlOrCfg === "string"
            ? urlOrCfg
            : { base: resolveProxyBase(), params: urlOrCfg?.params }
    );

    const headers = await buildProxyHeaders(extra.headers);
    // GET has no body: TB Mule's DataWeave blind-parses the payload as JSON when
    // Content-Type: application/json is present, and 500s on empty input.
    delete headers["Content-Type"];

    const r = await fetchWithTimeout(
        url,
        {
            method: "GET",
            headers,
            credentials: "omit",
            ...extra.fetchInit,
        },
        "generic"
    );

    const parsed = await safeReadJsonOrText(r);

    if (!r.ok) throw errorFromResponse(r.status, r.statusText, parsed);

    return unwrapLambdaProxyPayload(parsed);
}

/* =======================
   PROXY POST (JWT)
   ======================= */
export async function proxyPostJson(urlOrCfg, body, extra = {}) {
    const url = toUrl(
        typeof urlOrCfg === "string"
            ? urlOrCfg
            : { base: resolveProxyBase(), params: urlOrCfg?.params }
    );

    const headers = await buildProxyHeaders(extra.headers);

    const r = await fetchWithTimeout(
        url,
        {
            method: "POST",
            headers,
            body:  JSON.stringify(withLoginBody(body || {})),
            credentials: "omit",
            ...extra.fetchInit,
        },
        "generic"
    );

    const parsed = await safeReadJsonOrText(r);

    if (!r.ok) throw errorFromResponse(r.status, r.statusText, parsed);

    return unwrapLambdaProxyPayload(parsed);
}

/* =======================
   PROXY JWT SMOKE TEST
   (for proxy team debugging)
   ======================= */
export async function proxyJwtSmokeTest() {
    const testUrl = read(
        "VITE_PROXY_JWT_TEST_URL",
        read("REACT_APP_PROXY_JWT_TEST_URL", "")
    ).trim();

    if (!testUrl) {
        throw new Error("PROXY_JWT_TEST_URL not configured");
    }

    const headers = await buildProxyHeaders();

    const r = await fetchWithTimeout(
        normalizeEndpoint(testUrl),
        {
            method: "GET",
            headers,
            credentials: "omit",
        },
        "generic"
    );

    const text = await r.text();

    if (!r.ok) {
        const err = new Error(text || "Proxy JWT validation failed");
        err.status = r.status;
        throw err;
    }

    try {
        return JSON.parse(text);
    } catch {
        return { message: text };
    }
}

const read = (k, d = "") =>
    (typeof import.meta !== "undefined" && import.meta.env && k in import.meta.env
        ? import.meta.env[k]
        : process.env[k]) ?? d;

function hasProtocol(u = "") { return /^https?:\/\//i.test(u); }
function normalizeBase(u = "") { if (!u) return ""; return hasProtocol(u) ? u.replace(/\/$/, "") : `https://${u.replace(/\/$/, "")}`; }
function normalizeEndpoint(u = "") { if (!u) return ""; return hasProtocol(u) ? u : `https://${u}`; }

function normalizeParamValue(v) {
    if (v === undefined || v === null) return "";
    const s = String(v);
    try { return decodeURIComponent(s); } catch { return s; }
}

export function buildUrl(base, params = {}) {
    const b = normalizeEndpoint(base).replace(/\/$/, "");
    const origin = (typeof window !== "undefined" && window.location && window.location.origin) || "http://localhost";
    const u = new URL(b, origin);
    if (params && typeof params === "object") {
        for (const [k, v] of Object.entries(params)) {
            if (v === undefined || v === null || v === "") continue;
            u.searchParams.set(k, normalizeParamValue(v));
        }
    }
    return u.toString();
}

export function normalizeFullUrl(input) {
    const raw = typeof input === "string" ? input : input?.toString?.() || String(input || "");
    try {
        const u = new URL(raw, (typeof window !== "undefined" ? window.location.origin : "http://localhost"));
        const sp = new URLSearchParams(u.search);
        let changed = false;
        for (const [k, v] of sp.entries()) {
            if (/%25[0-9A-Fa-f]{2}/.test(v)) {
                const once = v.replace(/%25([0-9A-Fa-f]{2})/g, "%$1");
                if (once !== v) { sp.set(k, once); changed = true; }
            }
        }
        if (changed) { u.search = sp.toString(); return u.toString(); }
        return u.toString();
    } catch { return raw; }
}

function toUrl(input) {
    if (typeof input === "string") return normalizeFullUrl(input);
    if (input && typeof input === "object" && input.base) return normalizeFullUrl(buildUrl(input.base, input.params || {}));
    return normalizeFullUrl(String(input || ""));
}

/* ===== helpers to read .env lists ===== */
const asList = (key) => {
    const raw = read(`VITE_${key}`, read(`REACT_APP_${key}`, ""));
    return String(raw || "")
        .split(/[, \t\r\n]+/)
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
};

/* ===== helpers for typed env ===== */
const envStr = (key, fallback = "") => String(read(`VITE_${key}`, read(`REACT_APP_${key}`, fallback)) ?? "").trim();
const envNum = (key, fallback = "") => {
    const raw = read(`VITE_${key}`, read(`REACT_APP_${key}`, fallback));
    const n = Number(String(raw ?? "").trim());
    return Number.isFinite(n) ? n : null;
};

/* ===== ENV (unchanged + role-group IDs) ===== */
const BASE_API_TOKEN = read("VITE_API_TOKEN", read("REACT_APP_API_TOKEN", ""));
const BASE_CLIENT_ID = read("VITE_CLIENT_ID", read("REACT_APP_CLIENT_ID", "1"));
const RAW_HEADER_FLAGS = {
    USE_AUTH_HEADERS: read("VITE_USE_AUTH_HEADERS", read("REACT_APP_USE_AUTH_HEADERS", "false")),
    USE_TB_AUTH_HEADERS: read("VITE_USE_TB_AUTH_HEADERS", read("REACT_APP_USE_TB_AUTH_HEADERS", "")),
    USE_HINGE_AUTH_HEADERS: read("VITE_USE_HINGE_AUTH_HEADERS", read("REACT_APP_USE_HINGE_AUTH_HEADERS", ""))
};
function toBool(str, def = false) {
    if (str == null || str === "") return def;
    const v = String(str).trim().toLowerCase();
    if (v === "true") return true;
    if (v === "false") return false;
    return def;
}

export const ENV = {
    TB_BASE: normalizeBase(read("VITE_TB_BASE", read("REACT_APP_TB_BASE", ""))),
    // New: DIRECTV TeamBridge Cookie-auth base (SSO OIDC -> /api/login -> cookies)
    TB_COOKIE_BASE: normalizeBase(read("VITE_TB_COOKIE_BASE", read("REACT_APP_TB_PROXY_BASE", "https://api.dev.directv.com/dtvcs-teambridge-api"))),
    // If true, TB/hinge requests rely on HttpOnly session cookie (no Bearer tokens on API calls)
    TB_COOKIE_MODE: toBool(read("VITE_TB_COOKIE_MODE", read("REACT_APP_TB_COOKIE_MODE", "true")), true),
    HINGE_BASE: normalizeBase(read("VITE_HINGE_BASE", read("REACT_APP_HINGE_BASE", ""))),
    LOOKUP_URL: normalizeEndpoint(read("VITE_LOOKUP_URL", read("REACT_APP_LOOKUP_URL", ""))),
    TB_AGG_URL: normalizeEndpoint(read("VITE_TB_AGG_URL", read("REACT_APP_TB_AGG_URL", ""))),
    TB_NOTIF_URL: normalizeEndpoint(read("VITE_TB_NOTIF_URL", read("REACT_APP_TB_NOTIF_URL", ""))),
    ONBOARDING_NEW_URL: normalizeEndpoint(read("VITE_ONBOARDING_NEW_URL", read("REACT_APP_ONBOARDING_NEW_URL", ""))),
    ONBOARDING_EDIT_URL: normalizeEndpoint(read("VITE_ONBOARDING_EDIT_URL", read("REACT_APP_ONBOARDING_EDIT_URL", ""))),
    ONBOARDING_DRAFT_URL: normalizeEndpoint(read("VITE_ONBOARDING_DRAFT_URL", read("REACT_APP_ONBOARDING_DRAFT_URL", ""))),
    ONBOARDING_SOWS_URL: normalizeEndpoint(read("VITE_ONBOARDING_SOWS_URL", read("REACT_APP_ONBOARDING_SOWS_URL", ""))),
    ONBOARDING_DROPDOWNS_URL: normalizeEndpoint(read("VITE_ONBOARDING_DROPDOWNS_URL", read("REACT_APP_ONBOARDING_DROPDOWNS_URL", ""))),
    API_TOKEN: BASE_API_TOKEN,
    CLIENT_ID: BASE_CLIENT_ID,
    TB_API_TOKEN: read("VITE_TB_API_TOKEN", read("REACT_APP_TB_API_TOKEN", BASE_API_TOKEN)),
    TB_CLIENT_ID: read("VITE_TB_CLIENT_ID", read("REACT_APP_TB_CLIENT_ID", BASE_CLIENT_ID)),
    HINGE_API_TOKEN: read("VITE_HINGE_API_TOKEN", read("REACT_APP_HINGE_API_TOKEN", BASE_API_TOKEN)),
    HINGE_CLIENT_ID: read("VITE_HINGE_CLIENT_ID", read("REACT_APP_HINGE_CLIENT_ID", BASE_CLIENT_ID)),
    MANAGE_REQUESTS_URL: normalizeEndpoint(read("VITE_MANAGE_REQUESTS_URL", read("REACT_APP_MANAGE_REQUESTS_URL", ""))),
    OFFBOARD_URL: normalizeEndpoint(read("VITE_OFFBOARD_URL", read("REACT_APP_OFFBOARD_URL", ""))),
    DELETE_URL: normalizeEndpoint(read("VITE_DELETE_URL", read("REACT_APP_DELETE_URL", ""))),
    TB_SEARCH_URL: normalizeEndpoint(read("VITE_TB_SEARCH_URL", read("REACT_APP_TB_SEARCH_URL", ""))),
    HINGE_SEARCH_URL: normalizeEndpoint(read("VITE_HINGE_SEARCH_URL", read("REACT_APP_HINGE_SEARCH_URL", ""))),
    APPROVALS_LIST_URL: normalizeEndpoint(read("VITE_APPROVALS_LIST_URL", read("REACT_APP_APPROVALS_LIST_URL", ""))),
    APPROVAL_DETAILS_URL: normalizeEndpoint(read("VITE_APPROVAL_DETAILS_URL", read("REACT_APP_APPROVAL_DETAILS_URL", ""))),
    REMINDER_URL: normalizeEndpoint(read("VITE_REMINDER_URL", read("REACT_APP_REMINDER_URL", ""))),
    APPROVAL_ACTION_URL: normalizeEndpoint(read("VITE_APPROVAL_ACTION_URL", read("REACT_APP_APPROVAL_ACTION_URL", ""))),
    CINGLEPOINT_URL: normalizeEndpoint(read("VITE_CINGLEPOINT_URL", read("REACT_APP_CINGLEPOINT_URL", ""))),
    SUPPLIER_VALIDATION_URL: normalizeEndpoint(read("VITE_SUPPLIER_VALIDATION_URL", read("REACT_APP_SUPPLIER_VALIDATION_URL", ""))),
    HINGE_SPONSOR_VALIDATE_URL: normalizeEndpoint(read("VITE_HINGE_SPONSOR_VALIDATE_URL", read("REACT_APP_HINGE_SPONSOR_VALIDATE_URL", ""))),
    HINGE_WEBPHONE_VALIDATE_URL: normalizeEndpoint(read("VITE_HINGE_WEBPHONE_VALIDATE_URL", read("REACT_APP_HINGE_WEBPHONE_VALIDATE_URL", ""))),
    HINGE_ROLE_LIST_URL: normalizeEndpoint(read("VITE_HINGE_ROLE_LIST_URL", read("REACT_APP_HINGE_ROLE_LIST_URL", ""))),
    HINGE_DTV_LOOKUP_URL: normalizeEndpoint(read("VITE_HINGE_DTV_LOOKUP_URL", read("REACT_APP_HINGE_DTV_LOOKUP_URL", ""))),
    ENABLE_HINGE_DETAIL: (() => toBool(read("REACT_APP_ENABLE_HINGE_DETAIL", read("VITE_ENABLE_HINGE_DETAIL", "false")), false))(),
    USE_AUTH_HEADERS: toBool(RAW_HEADER_FLAGS.USE_AUTH_HEADERS, false),
    USE_TB_AUTH_HEADERS: (() => toBool(RAW_HEADER_FLAGS.USE_TB_AUTH_HEADERS, toBool(RAW_HEADER_FLAGS.USE_AUTH_HEADERS, false)))(),
    USE_HINGE_AUTH_HEADERS: (() => toBool(RAW_HEADER_FLAGS.USE_HINGE_AUTH_HEADERS, toBool(RAW_HEADER_FLAGS.USE_AUTH_HEADERS, false)))(),
    ENABLE_HINGE: (() => toBool(read("REACT_APP_ENABLE_HINGE", read("VITE_ENABLE_HINGE", "false")), false))(),
    ENABLE_SPONSOR_VALIDATION: (() => toBool(read("REACT_APP_ENABLE_SPONSOR_VALIDATION", read("VITE_ENABLE_SPONSOR_VALIDATION", "false")), false))(),
    ENABLE_WEBPHONE_VALIDATION: (() => toBool(read("REACT_APP_ENABLE_WEBPHONE_VALIDATION", read("VITE_ENABLE_WEBPHONE_VALIDATION", "false")), false))(),
    ENABLE_HINGE_ROLE_DROPDOWN: (() => toBool(read("REACT_APP_ENABLE_HINGE_ROLE_DROPDOWN", read("VITE_ENABLE_HINGE_ROLE_DROPDOWN", "false")), false))(),
    IS_REACTIVATE_ATTUID_NEEDED: (() => toBool(read("REACT_APP_IS_REACTIVATE_ATTUID_NEEDED", read("VITE_IS_REACTIVATE_ATTUID_NEEDED", "true")), true))(),
    IS_VALIDATE_CP_NEEDED: (() => toBool(read("REACT_APP_IS_VALIDATE_CP_NEEDED", read("VITE_IS_VALIDATE_CP_NEEDED", "true")), true))(),
    CP_EMAIL_ALLOWED: (() => toBool(read("REACT_APP_CP_EMAIL_ALLOWED", read("VITE_CP_EMAIL_ALLOWED", "false")), false))(),
    ENABLE_DTV_PREFILL: (() => toBool(read("REACT_APP_ENABLE_DTV_PREFILL", read("VITE_ENABLE_DTV_PREFILL", "false")), false))(),
    FETCH_TIMEOUT_MS: Number(read("VITE_FETCH_TIMEOUT_MS", read("REACT_APP_FETCH_TIMEOUT_MS", "60000"))),
    IDLE_TIMEOUT_MIN: Number(read("VITE_IDLE_TIMEOUT_MIN", read("REACT_APP_IDLE_TIMEOUT_MIN", "30"))),
    ABSOLUTE_SESSION_MIN: Number(read("VITE_ABSOLUTE_SESSION_MIN", read("REACT_APP_ABSOLUTE_SESSION_MIN", "480"))),
    PAGE_SIZE_DEFAULT: Number(read("VITE_PAGE_SIZE_DEFAULT", read("REACT_APP_PAGE_SIZE_DEFAULT", "100"))),
    DEFAULT_DTVID: read("VITE_DEFAULT_DTVID", read("REACT_APP_DEFAULT_DTVID", "")),
    FETCH_CREDENTIALS: read("VITE_FETCH_CREDENTIALS", read("REACT_APP_FETCH_CREDENTIALS", "omit")),
    EXCEPTIONS_LIST_URL: normalizeEndpoint(read("VITE_EXCEPTIONS_LIST_URL", read("REACT_APP_EXCEPTIONS_LIST_URL", ""))),
    EXCEPTION_RETRY_URL: normalizeEndpoint(read("VITE_EXCEPTION_RETRY_URL", read("REACT_APP_EXCEPTION_RETRY_URL", ""))),

    S2P_CONTRACT_VALIDATION_URL: normalizeEndpoint(read("VITE_S2P_CONTRACT_VALIDATION_URL", read("REACT_APP_S2P_CONTRACT_VALIDATION_URL", ""))),
    S2P_PURCHASEORDER_VALIDATION_URL: normalizeEndpoint(read("VITE_S2P_PURCHASEORDER_VALIDATION_URL", read("REACT_APP_S2P_PURCHASEORDER_VALIDATION_URL", ""))),
    S2P_POLINES_VALIDATION_URL: normalizeEndpoint(read("VITE_S2P_POLINES_VALIDATION_URL", read("REACT_APP_S2P_POLINES_VALIDATION_URL", ""))),

    /* 🔐 Group→Role mapping (IDs may be comma/space separated)
       FTE Nominator is identified via GROUP_FTE_NOMINATOR_IDS env var
       (same pattern as Employee Sponsor group key).
       CP Channel Delegate is NOT group-based — it comes from the
       Hinge profile attribute isChannelDelegate=true (see RequireAuth). */
    ROLE_GROUPS: {
        "DTV OPS Admin": asList("GROUP_DTV_OPS_ADMIN_IDS"),
        "Employee Supplier Admin": asList("GROUP_EMP_SUPPLIER_ADMIN_IDS"),
        "Employee Sponsor": asList("GROUP_EMP_SPONSOR_IDS"),
        "Nominator": asList("GROUP_NOMINATOR_IDS"),
        "FTE Nominator": asList("GROUP_FTE_NOMINATOR_IDS"),
        "Read Only": asList("GROUP_READ_ONLY_IDS"),
        // CP Channel Delegate: resolved from Hinge profile, not SSO group
    },

    NPW_EMAIL_NOTIFICATIONS_URL: normalizeEndpoint(
        read("VITE_NPW_EMAIL_NOTIFICATIONS_URL",
            read("REACT_APP_NPW_EMAIL_NOTIFICATIONS_URL"))
    ),
    CP_DEALER_ID: envNum("CP_DEALER_ID", ""),
    CP_DEALER_NAME: envStr("CP_DEALER_NAME", ""),
    CP_SELLING_LOCATION_ID: envNum("CP_SELLING_LOCATION_ID", ""),
    CP_SELLING_LOCATION_NAME: envStr("CP_SELLING_LOCATION_NAME", ""),
    CP_INFO_GROUPID: envStr("CP_INFO_GROUPID", ""),

    // Non-US CP defaults (used when country != United States)
    CP_NONUS_DEALER_ID: envNum("CP_NONUS_DEALER_ID", ""),
    CP_NONUS_DEALER_NAME: envStr("CP_NONUS_DEALER_NAME", ""),
    CP_NONUS_SELLING_LOCATION_ID: envNum("CP_NONUS_SELLING_LOCATION_ID", ""),
    CP_NONUS_SELLING_LOCATION_NAME: envStr("CP_NONUS_SELLING_LOCATION_NAME", ""),
    CP_NONUS_INFO_GROUPID: envStr("CP_NONUS_INFO_GROUPID", ""),


    TB_HINGE_PERSON_LIST_URL: normalizeEndpoint(read("VITE_TB_HINGE_PERSON_LIST_URL", read("REACT_APP_TB_HINGE_PERSON_LIST_URL", ""))),
    TB_HINGE_LOOKUP_URL: normalizeEndpoint(read("VITE_TB_HINGE_LOOKUP_URL", read("REACT_APP_TB_HINGE_LOOKUP_URL", ""))),
    TB_HINGE_ROLE_LIST_URL: normalizeEndpoint(read("VITE_TB_TB_HINGE_ROLE_LIST_URL", read("REACT_APP_TB_HINGE_ROLE_LIST_URL", ""))),
    TB_HINGE_SPONSOR_VALIDATE_URL: normalizeEndpoint(read("VITE_TB_TB_HINGE_SPONSOR_VALIDATE_URL", read("REACT_APP_TB_HINGE_SPONSOR_VALIDATE_URL", ""))),
    TB_HINGE_WEBPHONE_VALIDATE_URL: normalizeEndpoint(read("VITE_TB_TB_HINGE_WEBPHONE_VALIDATE_URL", read("REACT_APP_TB_HINGE_WEBPHONE_VALIDATE_URL", ""))),
    TB_HINGE_SEARCH_URL: normalizeEndpoint(read("VITE_TB_TB_HINGE_PERSON_SEARCH_URL", read("REACT_APP_TB_HINGE_PERSON_SEARCH_URL", ""))),
    CP_COUNTRYLIST_URL: normalizeEndpoint(read("VITE_CP_COUNTRYLIST_URL", read("REACT_APP_CP_COUNTRYLIST_URL", ""))),
    CP_VALIDATE_URL: normalizeEndpoint(read("VITE_CP_VALIDATE_URL", read("REACT_APP_CP_VALIDATE_URL", ""))),


};

/* ---------- STATIC-ASSET SAFETY LAYER ---------- */
function isLikelyStatic(url) {
    try {
        const u = new URL(url, (typeof window !== "undefined" ? window.location.origin : "http://localhost"));
        const p = u.pathname.toLowerCase();
        if (/\.(png|jpg|jpeg|svg|gif|ico|webp|bmp)$/.test(p)) return true;
        if (p.startsWith("/assets/") || p.includes("/static/")) return true;
        return false;
    } catch { return false; }
}


/* Logged-in identity (dtvId + role) appended to every backend request. */
function readLoginIdentity() {
    try {
        if (typeof window === "undefined") return { dtvId: "", role: "" };
        const ss = window.sessionStorage;
        return {
            dtvId: String(ss?.getItem("tb_dtvid") || "").trim(),
            role: String(ss?.getItem("tb_role") || "").trim(),
        };
    } catch {
        return { dtvId: "", role: "" };
    }
}

// Append the logged-in user's dtvId and role as query params (kept alongside existing params).
function appendLoginParams(url) {
    try {
        const { dtvId, role } = readLoginIdentity();
        if (!dtvId && !role) return url;
        const u = new URL(url, (typeof window !== "undefined" ? window.location.origin : "http://localhost"));
        if (dtvId && !u.searchParams.has("loggedInUserDtvId")) u.searchParams.set("loggedInUserDtvId", dtvId);
        if (role && !u.searchParams.has("loggedInUserRole")) u.searchParams.set("loggedInUserRole", role);
        return u.toString();
    } catch {
        return url;
    }
}

// Inject the logged-in user's dtvId and role into POST JSON bodies (kept alongside existing keys).
function withLoginBody(body) {
    if (!body || typeof body !== "object" || Array.isArray(body)) return body;
    const { dtvId, role } = readLoginIdentity();
    if (!dtvId && !role) return body;
    const next = { ...body };
    if (dtvId && next.loggedInUserDtvId == null) next.loggedInUserDtvId = dtvId;
    if (role && next.loggedInUserRole == null) next.loggedInUserRole = role;
    return next;
}

/* ---------------------------------------------------------------------------
   5. Request ID + headers (skip auth for static assets)
--------------------------------------------------------------------------- */
function uuid4() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (crypto?.getRandomValues?.(new Uint8Array(1))[0] ?? Math.floor(Math.random() * 256)) & 15;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}
export function newReqId() { return `tb-${uuid4()}`; }

function maybeMergeAuth(url, headers, system) {
    // system: "tb" | "hinge" | "generic"
    if (isLikelyStatic(url)) {
        // Do NOT attach Authorization to static assets
        return { ...headers };
    }
    // In cookie mode, do NOT attach Bearer auth headers to TB/Hinge API calls.
    // The session cookie (HttpOnly) is the source of truth after /api/login.
    if (ENV.TB_COOKIE_MODE && (system === "tb" || system === "hinge")) {
        return { "X-Request-ID": newReqId(), "X-Client-ID": String(ENV.TB_CLIENT_ID || ENV.CLIENT_ID || "1"), ...headers };
    }
    if (system === "tb" && ENV.USE_TB_AUTH_HEADERS) {
        return {
            Authorization: `Bearer ${ENV.TB_API_TOKEN || ENV.API_TOKEN}`,
            "X-Request-ID": newReqId(),
            "X-Client-ID": String(ENV.TB_CLIENT_ID || ENV.CLIENT_ID || "1"),
            ...headers
        };
    }
    if (system === "hinge" && ENV.USE_HINGE_AUTH_HEADERS) {
        return {
            Authorization: `Bearer ${ENV.HINGE_API_TOKEN || ENV.API_TOKEN}`,
            "X-Request-ID": newReqId(),
            "X-Client-ID": String(ENV.HINGE_CLIENT_ID || ENV.CLIENT_ID || "1"),
            ...headers
        };
    }
    if (system === "generic" && ENV.USE_AUTH_HEADERS) {
        return {
            Authorization: `Bearer ${ENV.API_TOKEN}`,
            "X-Request-ID": newReqId(),
            "X-Client-ID": String(ENV.CLIENT_ID || "1"),
            ...headers
        };
    }
    return { ...headers };
}

/* ---------------------------------------------------------------------------
   6. Fetch with timeout (normalized URL)
--------------------------------------------------------------------------- */

async function safeReadText(resp) {
    try { return await resp.text(); } catch { return ""; }
}

/**
 * Safely read and parse a response body.
 * - Never throws on empty body (""), 204, or bad JSON.
 * - Reads the body exactly once.
 * - Returns:
 *    * object/array for JSON bodies
 *    * string for non-JSON bodies
 *    * null for empty bodies
 */
async function safeReadJsonOrText(resp) {
    try {
        const ct = String(resp?.headers?.get?.("content-type") || "").toLowerCase();
        const txt = await resp.text();
        if (!txt || !txt.trim()) return null;

        // If server declares JSON, parse strictly; otherwise try-parse as JSON once, fall back to text.
        if (ct.includes("application/json")) {
            try { return JSON.parse(txt); } catch { return txt; }
        }
        try { return JSON.parse(txt); } catch { return txt; }
    } catch {
        return null;
    }
}

function errorFromResponse(status, statusText, parsedOrText) {
    let msg = `${status} ${statusText}`;
    if (parsedOrText) {
        if (typeof parsedOrText === "string" && parsedOrText.trim()) {
            msg = parsedOrText.trim();
        } else if (typeof parsedOrText === "object") {
            // Prefer .message, then .error.message, then .error
            msg = parsedOrText.message
                || (parsedOrText.error && typeof parsedOrText.error === "object" && parsedOrText.error.message)
                || (parsedOrText.error && typeof parsedOrText.error === "string" && parsedOrText.error)
                || JSON.stringify(parsedOrText);
        }
    }

    const err = new Error(msg);
    err.status = status;
    // keep raw for debugging
    err.body = parsedOrText;
    return err;
}

async function fetchWithTimeout(urlInput, options = {}, system = "generic") {
    // const url = toUrl(urlInput);
    const rawUrl = toUrl(urlInput);
    const staticAsset = isLikelyStatic(rawUrl);
    // Attach logged-in dtvId + role to all backend calls (never to static assets).
    const url = staticAsset ? rawUrl : appendLoginParams(rawUrl);
    const controller = new AbortController();
    const timeoutMs = ENV.FETCH_TIMEOUT_MS || 60000;
    const t = setTimeout(() => controller.abort(new Error("Request timeout")), timeoutMs);
    try {
        const headers = maybeMergeAuth(url, options.headers || {}, system);
        const defaultCreds = (system === "tb" || system === "hinge") ? "include" : ENV.FETCH_CREDENTIALS;
        const creds = isLikelyStatic(url) ? "same-origin" : (options.credentials ?? defaultCreds);
        const r = await fetch(url, { ...options, headers, credentials: creds, signal: controller.signal });
        return r;
    } finally {
        clearTimeout(t);
    }
}

/* ---------------------------------------------------------------------------
   6b. Cookie-session recovery + single retry
--------------------------------------------------------------------------- */

function isAuthRelatedStatus(status) {
    // Common patterns behind gateways/proxies when the session cookie is missing/expired.
    return status === 401 || status === 403 || status === 419 || status === 440;
}

async function tbFetchWithReauth(urlOrCfg, init, system) {
    // 1) Ensure cookie exists at least once
    await ensureTeamBridgeCookieSession(false);

    const first = await fetchWithTimeout(urlOrCfg, init, system);
    if (first.ok || !isAuthRelatedStatus(first.status)) return first;

    // 2) Cookie likely missing/expired/not-sent — refresh MSAL token + force cookie refresh, then retry once.
    try {
        // Try to get a fresh access token first (silent refresh) so /api/login uses a valid token.
        try { await getAccessTokenAuto({ minTtlMs: 5 * 60 * 1000, forceRefresh: true }); } catch { }
        await ensureTeamBridgeCookieSession(true);
    } catch (e) {
        // If refresh failed, return original response for richer error handling upstream.
        // eslint-disable-next-line no-console
        console.warn(`[TB] forced /api/login failed before retry (${system}):`, e?.message || e);
        return first;
    }

    const retryInit = {
        ...init,
        headers: { ...(init?.headers || {}), "X-Request-ID": newReqId() },
    };

    // eslint-disable-next-line no-console
    console.warn(`[TB] ${system} request got ${first.status}; forced /api/login and retrying once...`);

    return await fetchWithTimeout(urlOrCfg, retryInit, system);
}

function unwrapLambdaProxyPayload(data) {
    if (!data || typeof data !== "object") return data;
    if (!("body" in data)) return data;
    // Heuristic: AWS Lambda proxy integrations typically return { statusCode, headers, body, isBase64Encoded }
    const looksLikeLambdaProxy =
        "statusCode" in data || "isBase64Encoded" in data || "multiValueHeaders" in data;
    if (!looksLikeLambdaProxy) return data;

    const body = data.body;
    if (body == null) return data;
    if (typeof body === "object") return body;
    if (typeof body !== "string") return data;

    const text = body.trim();
    if (!text) return body;
    try {
        return JSON.parse(text);
    } catch {
        return body;
    }
}

/* ---------------------------------------------------------------------------
   7. JSON + FILE helpers
--------------------------------------------------------------------------- */
export async function getJson(urlOrCfg, extra = {}) {
    const credentials = extra.credentials || "include";
    const init = { method: "GET", headers: extra.headers, credentials, ...extra.fetchInit };
    // Cookie-based (credentials:"include") calls must ride the TB session: ensure a
    // valid, non-expired token + fresh cookie first (with one-shot 401 reauth retry).
    // Explicit credentials:"omit" (public/static) fetches skip auth entirely.
    const r = credentials === "omit"
        ? await fetchWithTimeout(urlOrCfg, init, "generic")
        : await tbFetchWithReauth(urlOrCfg, init, "generic");
    const parsed = await safeReadJsonOrText(r);
    if (!r.ok) throw errorFromResponse(r.status, r.statusText, parsed);
    return unwrapLambdaProxyPayload(parsed);
}
export async function postJson(urlOrCfg, body, extra = {}) {
    const credentials = extra.credentials || "include";
    const init = {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(extra.headers || {}) },
        body: JSON.stringify(withLoginBody(body || {})),
        credentials,
        ...extra.fetchInit
    };
    // Cookie-based calls ensure token/cookie validity first (with 401 reauth retry);
    // explicit credentials:"omit" skips auth.
    const r = credentials === "omit"
        ? await fetchWithTimeout(urlOrCfg, init, "generic")
        : await tbFetchWithReauth(urlOrCfg, init, "generic");
    const parsed = await safeReadJsonOrText(r);
    if (!r.ok) throw errorFromResponse(r.status, r.statusText, parsed);
    return unwrapLambdaProxyPayload(parsed);
}
export async function tbGetJson(urlOrCfg, extra = {}) {
    const init = { method: "GET", headers: extra.headers, credentials: extra.credentials || "include", ...extra.fetchInit };
    const r = await tbFetchWithReauth(urlOrCfg, init, "tb");
    const parsed = await safeReadJsonOrText(r);
    if (!r.ok) throw errorFromResponse(r.status, r.statusText, parsed);
    return unwrapLambdaProxyPayload(parsed);
}
export async function tbPostJson(urlOrCfg, body, extra = {}) {
    const init = {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(extra.headers || {}) },
        body: JSON.stringify(withLoginBody(body || {})),
        credentials: extra.credentials || "include",
        ...extra.fetchInit,
    };

    const r = await tbFetchWithReauth(urlOrCfg, init, "tb");
    const parsed = await safeReadJsonOrText(r);
    if (!r.ok) throw errorFromResponse(r.status, r.statusText, parsed);
    return unwrapLambdaProxyPayload(parsed);
}


export async function tbPostJsonWithBody(url, payload, options = {}) {
    const { fetchInit = {} } = options || {};
    const init = {
        method: fetchInit.method || "POST",
        headers: {
            "Content-Type": "application/json",
            ...(fetchInit.headers || {}),
        },
        // IMPORTANT: preserve the same credentials behavior as tbPostJson uses in your codebase.
        // If tbPostJson sets credentials:'include', keep it here as well:
        credentials: fetchInit.credentials || "include",
        body: JSON.stringify(withLoginBody(payload ?? {})),
        ...fetchInit,
    };

    const res = await tbFetchWithReauth(url, init, "tb");
    const parsed = await safeReadJsonOrText(res);
    if (!res.ok) throw errorFromResponse(res.status, res.statusText, parsed);
    return parsed;
}

export async function hingeGetJson(urlOrCfg, extra = {}) {
    const init = { method: "GET", headers: extra.headers, credentials: extra.credentials || "include", ...extra.fetchInit };
    const r = await tbFetchWithReauth(urlOrCfg, init, "hinge");
    const parsed = await safeReadJsonOrText(r);
    if (!r.ok) throw errorFromResponse(r.status, r.statusText, parsed);
    return unwrapLambdaProxyPayload(parsed);
}
export async function hingePostJson(urlOrCfg, body, extra = {}) {
    const init = {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(extra.headers || {}) },
        body: JSON.stringify(withLoginBody(body || {})),
        credentials: extra.credentials || "include",
        ...extra.fetchInit,
    };

    const r = await tbFetchWithReauth(urlOrCfg, init, "hinge");
    const parsed = await safeReadJsonOrText(r);
    if (!r.ok) throw errorFromResponse(r.status, r.statusText, parsed);
    return unwrapLambdaProxyPayload(parsed);
}

/* Binary/text helpers for static files (use these if you ever fetch assets via JS) */
export async function getFile(urlOrCfg, extra = {}) {
    const r = await fetchWithTimeout(
        urlOrCfg,
        { method: "GET", headers: extra.headers, credentials: extra.credentials || "include", ...extra.fetchInit },
        "generic"
    );
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.blob();
}
export async function getText(urlOrCfg, extra = {}) {
    const r = await fetchWithTimeout(
        urlOrCfg,
        { method: "GET", headers: extra.headers, credentials: extra.credentials || "include", ...extra.fetchInit },
        "generic"
    );
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.text();
}
export async function tbGetJsonLoose(url, opts = {}) {
    const res = await fetch(url, { method: "GET", headers: {}, ...opts });
    const text = await res.text();
    let data = null;
    if (text && text.trim()) {
        try { data = JSON.parse(text); } catch { data = { rawText: text }; }
    }

    // Accept {rows,total} even if HTTP is non-2xx
    if (!res.ok) {
        if (data && (Array.isArray(data.rows) || Number.isFinite(data.total))) return data;
        throw new Error(`HTTP ${res.status}`);
    }
    return data;
}
